import React from 'react';

import {
  Button,
  Col,
  Info,
  Input,
  Row,
  Select,
  Switcher,
  TreeSelector,
} from 'redpoints-front-components/index';
import { limitNumberOfElements } from 'redpoints-front-common/helpers';

import { translate } from '../../../helpers/languageHelper';
import { IMAGE_RECOGNITION_THRESHOLD } from './constants';

const COLUMN_WIDTH = {
  sm: 24,
  md: 24,
  xl: 8,
};

const formatPercentage = percent => (percent ? `${percent}%` : '');

const getTranslation = key =>
  translate(`tasks.image_recognition.filters.${key}`);

const getClassNameFromPercentage = percent => {
  if (percent === '') {
    return '';
  }

  return percent < IMAGE_RECOGNITION_THRESHOLD
    ? 'below-threshold'
    : 'above-threshold';
};

export const getTreeKeysSelected = selectedObject => {
  const response = [];
  [
    ...Object.entries(selectedObject)
      .map(([key, values]) =>
        [...(values || [])].map(value => `${key}_${value}`)
      )
      .filter(array => array.length !== 0),
  ].forEach(values => values.forEach(value => response.push(value)));
  return response;
};

const ImageRecognitionFilters = ({
  assetSelected,
  assetTypeData,
  disablePanel,
  domainPlatformData,
  fetchMoreData,
  filters,
  handleSwitchFetchData,
  incidentStatusData,
  incidentStatusSelected,
  machineLearningModelStats,
  onApplyFilters,
  onFilterChange,
  onResetFilters,
  resp,
  totalElements,
}) => {
  const {
    averagePrecision,
    itemsNegative,
    itemsPositive,
  } = machineLearningModelStats;

  const { incidentStatus, incidentStatusGroup } = incidentStatusSelected;

  const hasAssetSelected = assetSelected.key !== null;

  return (
    <div className="image-recognition-page__filters">
      <Row>
        <Col {...COLUMN_WIDTH}>
          <Select
            data={assetTypeData}
            label={getTranslation('asset')}
            onChange={value => onFilterChange(value, 'assetSelected')}
            value={assetSelected.key}
          />
        </Col>
        <Col {...COLUMN_WIDTH}>
          <Select
            data={domainPlatformData}
            disabled={!hasAssetSelected}
            label={getTranslation('domainPlatform')}
            mode="multiple"
            onChange={value => onFilterChange(value, 'domainPlatform')}
            value={filters.domainPlatform}
          />
        </Col>
        <Col {...COLUMN_WIDTH}>
          <TreeSelector
            disabled={!hasAssetSelected}
            label={getTranslation('incidentStatus')}
            onChange={value => onFilterChange(value, 'incidentStatusSelected')}
            placeholder={translate('common.all')}
            treeData={incidentStatusData}
            value={getTreeKeysSelected({
              incidentStatus,
              incidentStatusGroup,
            })}
          />
        </Col>
      </Row>
      <Row className="row-divider">
        <Col {...COLUMN_WIDTH}>
          <Input
            disabled={!hasAssetSelected}
            label={getTranslation('minScoring')}
            onChange={e => onFilterChange(e.target.value, 'minScoring')}
            type="number"
            value={filters.minScoring}
          />
        </Col>
        <Col {...COLUMN_WIDTH}>
          <Input
            disabled={!hasAssetSelected}
            label={getTranslation('maxScoring')}
            onChange={e => onFilterChange(e.target.value, 'maxScoring')}
            type="number"
            value={filters.maxScoring}
          />
        </Col>
        <Col {...COLUMN_WIDTH}>
          <Switcher
            checked={!fetchMoreData}
            onChange={handleSwitchFetchData}
            disabled={totalElements === 0}
            label={getTranslation('dontLoadMoreImages')}
          />
        </Col>
      </Row>
      <Row className="row-divider">
        <Col {...COLUMN_WIDTH}>
          <Info label={getTranslation('itemsPositive')} value={itemsPositive} />
        </Col>
        <Col {...COLUMN_WIDTH}>
          <Info label={getTranslation('itemsNegative')} value={itemsNegative} />
        </Col>
        <Col {...COLUMN_WIDTH}>
          <div className={getClassNameFromPercentage(averagePrecision)}>
            <Info
              label={getTranslation('averagePrecision')}
              value={formatPercentage(averagePrecision)}
            />
          </div>
        </Col>
      </Row>
      <Row className="row-divider">
        <Col {...COLUMN_WIDTH}>
          <Button
            type="primary"
            onClick={onApplyFilters}
            disabled={!hasAssetSelected}
          >
            {translate('actions.apply')}
          </Button>
          <Button
            type="secondary"
            onClick={onResetFilters}
            disabled={disablePanel}
          >
            {translate('actions.clear')}
          </Button>
        </Col>
        <Col {...COLUMN_WIDTH} offset={16}>
          <span className="image-recognition-page__filters-results">
            {limitNumberOfElements(resp.totalElements)}{' '}
            {translate('tasks.image_recognition.results')}
          </span>
        </Col>
      </Row>
    </div>
  );
};

export default ImageRecognitionFilters;
